import dayjs from 'dayjs'

export const SCREEN_TITLE = '반품관리'

/**
 * 서버 페이징이 없어 클라이언트 페이저만 쓴다(3.1 — 조건에 걸린 전건이 한 번에 온다).
 * 페이지 크기는 화면(생성본)이 정한다 — '몇 개씩' 미노출 = 10,000개씩(MOMS 공통 규칙 2026-08-24). URL 에는 이 값과 다를 때만 싣는다.
 */
export const DEFAULT_PAGE_SIZE = 10000

/** 다운로드 사유 모달 상세사유 최소 길이(3.4). */
export const REASON_DETAIL_MIN_LENGTH = 5

/** 다운로드 비밀번호 규칙(3.4, D9) — 8자 이상, 대문자·소문자·숫자·특수문자 각 1개 이상, 공백 불가. */
export const PASSWORD_RULE = {
  minLength: 8,
  pattern: /^(?=\S+$)(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/,
}

export const MESSAGES = {
  requiredFilter: '화주와 반품일자를 입력해 주세요.',
  searchNoRows: '조회된 데이터가 없습니다.',
  searchFailed: '조회 중 오류가 발생했습니다.',
  /** 저장 필수값 누락 — 어느 칸인지 채워 넣는다(옛 화면 message.error 문구 그대로). */
  saveMissingRequired: (title: string) => `${title}은(는) 필수입니다.`,
  saveNoTarget: '저장할 변경 사항이 없습니다.',
  saveSuccess: '저장했습니다.',
  confirmSuccess: '확정되었습니다.',
  confirmNone: '확정 대상이 없습니다.',
  attachRequiresSave: '저장 후 첨부할 수 있습니다.',
  excelNoRows: '다운로드할 데이터가 없습니다.',
  excelUploadEmpty: '가져올 데이터 행이 없습니다.',
  excelUploadNoFile: '업로드할 파일을 선택해 주세요.',
  shipperNameNotInType: '선택한 화주유형에 없는 화주입니다. 화주명을 다시 선택해 주세요.',
  confirmNeedsSearch: '조회 후 확정할 수 있습니다.',
  deleteSaved: '삭제했습니다.',
  deleteLocalOnly: '저장하지 않은 행을 목록에서 뺐습니다.',
  mediaUnreachable: '첨부·동영상은 화면 모달에서 관리합니다.',
} as const

/** 주문검색(송장 헬퍼) 출고일자 기간 — 옛 모달 기본값(오늘 기준 30일). 데모 모달에 날짜 칸이 없어 이 기간으로 고정해 보낸다(BE 필수). */
export const ORDER_SEARCH_DAYS = 30
export const ORDER_SEARCH_DATE_FORMAT = 'YYYYMMDD'

/** 다운로드 blob URL 해제 지연(ms) — Safari 가 다운로드를 비동기로 처리해 동기 해제면 URL 이 먼저 풀린다. */
export const BLOB_REVOKE_DELAY_MS = 100

/**
 * 1.0 API 회귀 테스트(D13) 허용 목록 — 비어 있다. 다운로드 사유코드 콤보(D7 예외)는 LOGISPRD-14161 에서
 * 2.0 `/api/v1/combos` 로 옮겼다. 새 예외는 이름을 여기 명시해 슬그머니 통과시키지 않는다.
 */
export const ALLOWED_LEGACY_URLS: string[] = []

/** 옛 폼 값 키 — URL/세션 복원(searchQuery)·조회 요청 변환(filterToRequest)이 쓴다. 뷰 조회영역 키와의 대응은 index.tsx toFormValues/toFilterValues. */
export const FIELD_NAMES = {
  logisticsServiceTypes: 'logisticsServiceTypes',
  /**
   * 화주코드. BE `shipper` 는 단일값이지만 폼 값은 배열로 온다 — pbo-core 0.7.8 `FilterArea` 가
   * `nameMode: 'single'` 을 `ShipperSelectField` 로 전달하지 않기 때문이다(LOGISPRD-12578 실측).
   * 변환은 `filterToRequest.toReturnManagementReq` 가 맡는다.
   */
  shipperCodes: 'shipperCodes',
  plnDateRange: 'plnDateRange',
  insBy: 'insBy',
  rlChannel: 'rlChannel',
} as const

export interface ReturnManagementFormValues {
  [FIELD_NAMES.logisticsServiceTypes]?: string[]
  /** `logistics.ShipperSelect` 화주명 값 — 배열로 오지만 복원 경로에서 문자열로 올 때도 받는다. */
  [FIELD_NAMES.shipperCodes]?: string[] | string
  [FIELD_NAMES.plnDateRange]?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  [FIELD_NAMES.insBy]?: string
  [FIELD_NAMES.rlChannel]?: string
}

/** 화주유형 기본 선택값. */
export const DEFAULT_LOGISTICS_SERVICE_TYPE = 'M1P'

/**
 * 화면 기본값 — 화주유형 = M1P, 화주 = 로그인 계정의 화주 코드, 반품일자 = 오늘~오늘(계획서 5장 2단계, D14).
 * 날짜는 호출 시점 기준으로 매번 계산한다(모듈 로드 시점에 얼려 두면 자정을 넘겨 열어 둔 탭에서 어긋난다).
 */
export const buildDefaultFilters = (shipperCode: string): ReturnManagementFormValues => ({
  logisticsServiceTypes: [DEFAULT_LOGISTICS_SERVICE_TYPE],
  shipperCodes: shipperCode,
  plnDateRange: [dayjs(), dayjs()],
  insBy: '',
  rlChannel: '',
})
